import { useState } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { User as UserIcon, Lock, AtSign } from "lucide-react";
import { Navigate } from "react-router-dom";

const Account = () => {
  const { user, refreshUser, loading } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  const saveName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    if (trimmed === user.name) { toast.info("No changes to save"); return; }
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      await refreshUser();
      toast.success("Name updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update name");
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    setSavingPwd(true);
    try {
      await api.patch("/auth/me", { current_password: currentPwd, new_password: newPwd });
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      toast.success("Password updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10 max-w-3xl" data-testid="account-page">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Your gear</div>
        <h1 className="font-heading text-4xl font-black text-mm-navy mt-2">My account</h1>
        <p className="text-mm-navy/70 mt-2">Update the name on your account or change your password.</p>

        <div className="mt-8 grid gap-6">
          {/* Profile card */}
          <form onSubmit={saveName} className="stage-card p-7" data-testid="account-profile-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-mm-steel/15 text-mm-navy flex items-center justify-center">
                <UserIcon size={18} strokeWidth={2.5} />
              </div>
              <h2 className="font-heading text-xl font-bold text-mm-navy">Profile</h2>
            </div>
            <div className="mt-5 grid gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Display name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                  data-testid="account-name-input"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel flex items-center gap-1.5"><AtSign size={12} strokeWidth={2.5} /> Email</label>
                <input
                  value={user.email}
                  disabled
                  className="mt-1.5 w-full bg-mm-parchment/60 border-2 border-mm-navy/10 rounded-lg px-4 py-3 text-mm-navy/60"
                  data-testid="account-email-input"
                />
                <p className="text-xs text-mm-navy/50 mt-1">Email changes aren't supported yet — contact support if you need a switch.</p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button type="submit" disabled={savingName} className="btn-trail btn-gold text-sm" data-testid="account-save-name-btn">
                {savingName ? "Saving..." : "Save name"}
              </button>
            </div>
          </form>

          {/* Password card */}
          <form onSubmit={savePassword} className="stage-card p-7" data-testid="account-password-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-mm-steel/15 text-mm-navy flex items-center justify-center">
                <Lock size={18} strokeWidth={2.5} />
              </div>
              <h2 className="font-heading text-xl font-bold text-mm-navy">Change password</h2>
            </div>
            <div className="mt-5 grid gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Current password</label>
                <input
                  type="password"
                  value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)}
                  className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                  data-testid="account-current-password-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">New password</label>
                  <input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    minLength={8}
                    className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                    data-testid="account-new-password-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Confirm new password</label>
                  <input
                    type="password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    minLength={8}
                    className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
                    data-testid="account-confirm-password-input"
                  />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="submit"
                disabled={savingPwd || !currentPwd || !newPwd}
                className="btn-trail btn-gold text-sm disabled:opacity-50"
                data-testid="account-save-password-btn"
              >
                {savingPwd ? "Updating..." : "Update password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Account;
